import {
  ListBase,
  Title,
  ListToolbar,
  BulkDeleteButton,
  BulkExportButton,
  EditButton,
  DeleteWithConfirmButton,
  TextInput,
  NullableBooleanInput,
  useListContext,
  SelectInput,
  useResourceDefinition,
} from "react-admin";
import { useForm, FormProvider } from "react-hook-form";
import { useEffect, useRef } from "react";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { brand } from "../../themes/brandTokens";
import { resourceNoun } from "./resourceNouns";
import { Card, Box, Grid, Button, InputAdornment, Tooltip } from "@mui/material";
import PageTitleWrapper from "./PageTitleWrapper";
import PageHeader from "./PageHeader";
import { Pagination } from "./Pagination";
import AnimatedBlock from "../Common/AnimatedBlock";
import { canAccess, IfCanAccess } from "@react-admin/ra-rbac";
import {
  TopToolbar,
  SelectColumnsButton,
  FilterButton,
  ExportButton,
  CreateButton,
  useGetList,
  Loading,
} from "react-admin";

export const AccountFilter = () => {
  const { data, isLoading, error } = useGetList("accounts");
  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return <p>ERROR</p>;
  }
  return (
    <IfCanAccess action="list" resource="accounts">
      <SelectInput
        label="Account"
        source="accountId"
        helperText={false}
        choices={data
        .map((account) => ({ id: account.id, name: account.name }))}
      />
    </IfCanAccess>
  );
};

export const BulkActionButtons = ({ ressource }: { ressource: string }) => (
  <>
    <IfCanAccess action="delete" resource={ressource}>
      <BulkDeleteButton />
    </IfCanAccess>
    <IfCanAccess action="export" resource={ressource}>
      <BulkExportButton />
    </IfCanAccess>
  </>
);

// Filters read as a row of grey pills; the label floats inside the pill once a value is set.
const filterBarSx = {
  "& .MuiFormControl-root": { m: 0, minWidth: 150 },
  "& .MuiInputBase-root": {
    borderRadius: 9999,
    backgroundColor: brand.surfaceMuted,
    minHeight: 40,
    pl: 1,
    transition: "background-color .15s",
  },
  "& .MuiInputBase-root:hover": { backgroundColor: "#eceff3" },
  "& .MuiInputBase-root.Mui-focused": { backgroundColor: "#ffffff" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
  "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
  "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: brand.blue },
  "& .MuiInputLabel-root": { left: 8 },
  "& .MuiInputLabel-root.MuiInputLabel-shrink": { left: 12 },
  "& .MuiOutlinedInput-notchedOutline legend": { ml: 1.5 },
  "& .MuiSelect-icon": { right: 12 },
};

const resetPillSx = {
  borderRadius: 9999,
  color: "text.secondary",
  px: 1.5,
  "&:hover": { color: "text.primary", backgroundColor: "rgba(23, 23, 23, 0.06)" },
};

export const FilterForm = ({ filters }: { filters: any[] }) => {
  const { displayedFilters, filterValues, setFilters, hideFilter } =
  useListContext();
  
  const form = useForm({
    defaultValues: filterValues,
  });
  
  const watchedValues = form.watch();
  const lastAppliedFilters = useRef({});
  
  useEffect(() => {
    const currentFilters = Object.keys(watchedValues).reduce((acc, key) => {
      if (watchedValues[key] !== undefined && watchedValues[key] !== null && watchedValues[key] !== '') {
        acc[key] = watchedValues[key];
      }
      return acc;
    }, {});

    if (JSON.stringify(currentFilters) !== JSON.stringify(lastAppliedFilters.current)) {
      lastAppliedFilters.current = currentFilters;
      if (Object.keys(currentFilters).length > 0) {
        setFilters(currentFilters, Object.keys(currentFilters));
      }
    }
  }, [watchedValues, setFilters]);
  
  const onSubmit = (values) => {
    if (Object.keys(values).length > 0) {
      setFilters(values, []);
    } else {
      hideFilter("main");
    }
  };
  
  const resetFilter = () => {
    form.reset({});
    setFilters({}, []);
  };
  
  if (filters.length === 0) return null;
  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Grid container spacing={1} alignItems="center" sx={filterBarSx}>
          {filters.map((filter, index) => (
            <Grid item key={index} sx={{ display: 'flex', alignItems: 'center' }}>
              {filter}
            </Grid>
          ))}
{/*           <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
            <Button variant="outlined" color="primary" type="submit" size="small" >
              Apply
            </Button>
          </Grid> */}
          {Object.keys(filterValues).length > 0 && (
            <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
              <Button onClick={resetFilter} size="small" startIcon={<RestartAltIcon />} sx={resetPillSx}>
                Reset
              </Button>
            </Grid>
          )}
        </Grid>
      </form>
    </FormProvider>
  );
};

export const ListActions = ({
  ressource,
  filters,
}: {
  ressource: string;
  filters: any[];
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, width: '100%', '& > .MuiToolbar-root': { flexShrink: 0, ml: 'auto' } }}>
    <FilterForm filters={filters} />
    <TopToolbar>
      <SelectColumnsButton />
      <IfCanAccess action="export" resource={ressource}>
        <ExportButton />
      </IfCanAccess>
      <IfCanAccess action="create" resource={ressource}>
        <CreateButton />
      </IfCanAccess>
    </TopToolbar>
  </Box>
);

// Row actions as icons (the row itself opens the record); shown on hover. Edit only
// appears for resources that have an edit view.
export const ListButtons = ({ ressource }: { ressource: string }) => {
  const { hasEdit } = useResourceDefinition({ resource: ressource });
  return (
    <Box className="row-actions" sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, opacity: 0.55, transition: "opacity 0.15s" }}>
      {hasEdit && ressource !== "cryptoOrders" && (
        <IfCanAccess action="edit" resource={ressource}>
          <Tooltip title="Edit">
            <span>
              <EditButton label="" icon={<EditOutlinedIcon />} sx={{ minWidth: 0, px: 1, "& .MuiButton-startIcon": { m: 0 } }} />
            </span>
          </Tooltip>
        </IfCanAccess>
      )}
      <IfCanAccess action="delete" resource={ressource}>
        <Tooltip title="Delete">
          <span>
            <DeleteWithConfirmButton
              label=""
              icon={<DeleteOutlineIcon />}
              confirmTitle={`Delete this ${resourceNoun(ressource)}?`}
              confirmContent="This cannot be undone."
          confirmColor="warning"
              sx={{ minWidth: 0, px: 1, "& .MuiButton-startIcon": { m: 0 } }}
            />
          </span>
        </Tooltip>
      </IfCanAccess>
    </Box>
  );
};

const ComponentList = ({
  children,
  actions,
  filters = [],
  title,
  subtitle,
  sort = { field: "ulid", order: "DESC" as any },
  icon = null,
  ...props
}) => {
  return (
    <Box
      sx={{
        marginTop: 0,
      }}
    >
      <PageTitleWrapper>
        <PageHeader title={title} subtitle={subtitle} icon={icon} />
      </PageTitleWrapper>
      <Grid
        sx={{
          px: { xs: 2, sm: 4 },
        }}
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={4}
      >
        <Grid item xs={12}>
          <AnimatedBlock>
            <ListBase {...props} sort={sort}>
              <Title title={title} />
              <Card>
                <Box px={2.5} py={2}>
                  <ListToolbar actions={actions} />
                </Box>
                {children}
              </Card>
              <Pagination />
            </ListBase>
          </AnimatedBlock>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ComponentList;
